<template>
	<div class="chat-input-area">
		<div
			:class="['edit-container', { 'is-empty': isEmpty, 'not-empty': !isEmpty }]"
			:data-placeholder="placeholderText"
			:style="editorStyle"
			:contenteditable="!disabled"
			:tabindex="disabled ? -1 : 0"
			:aria-disabled="disabled"
			@paste.prevent="onPaste"
			@keydown="onKeydown" @compositionstart="compositionFlag = true" @compositionend="onCompositionEnd"
			@input="onEditorInput" @mousedown="onMousedown" ref="content" @keyup="onKeyup" @click="onClickInput">
		</div>
		<chat-at-box @select="onAtSelect" :search-text="atSearchText" ref="atBox" :group="group"
			:members="groupMembers"></chat-at-box>
	</div>
</template>

<script>
import ChatAtBox from "./ChatAtBox";
import { createMediaClipboardFile, readMediaClipboardEntry } from "../../utils/mediaClipboard";
import { getFileExtension, resolveFileTypeMeta } from "../../utils/fileTypeMeta";

export default {
	name: "ChatInput",
	components: { ChatAtBox },
	props: {
		disabled: {
			type: Boolean,
			default: false
		},
		group: {
			type: Object
		},
		groupMembers: {
			type: Array
		},
		placeholder: {
			type: String,
			default: "聊点什么吧~"
		},
	},
	data() {
		return {
			draftMap: new Map(),
			imageList: [],
			fileList: [],
			currentId: 0,
			atSearchText: null,
			compositionFlag: false,
			atIng: false,
			isEmpty: true,
			blurRange: null,
			editorHeight: 0
		}
	},
	computed: {
		placeholderText() {
			return this.disabled ? "" : (this.placeholder || "聊点什么吧~");
		},
		editorStyle() {
			if (!this.editorHeight) {
				return null;
			}
			return {
				height: `${this.editorHeight}px`
			};
		}
	},
	watch: {
		disabled(value) {
			if (value) this.blurEditor();
		}
	},
	mounted() {
		this.empty(false);
		this.$nextTick(() => {
			this.syncEditorHeight();
		});
	},
	methods: {
		blurEditor() {
			const content = this.$refs.content;
			if (content) {
				content.blur();
				const selection = window.getSelection();
				if (selection && (content.contains(selection.anchorNode) || content.contains(selection.focusNode))) {
					selection.removeAllRanges();
				}
			}
			this.blurRange = null;
			this.compositionFlag = false;
			this.atIng = false;
			this.atSearchText = null;
			this.$refs.atBox?.close();
		},
		async onPaste(e) {
			if (this.disabled) return;
			this.isEmpty = false;
			const clipboardData = e.clipboardData || window.clipboardData;
			const range = this.ensureSelectionRange();
			if (!range) {
				return;
			}
			if (range.startContainer !== range.endContainer || range.startOffset !== range.endOffset) {
				range.deleteContents();
			}
			const nativeFiles = this.extractClipboardFiles(clipboardData);
			if (nativeFiles.length > 0) {
				nativeFiles.forEach((file) => this.insertClipboardFile(file));
				return;
			}
			const txt = clipboardData && typeof clipboardData.getData === 'function'
				? clipboardData.getData('Text')
				: '';
			const mediaEntry = await readMediaClipboardEntry({
				clipboardText: txt
			});
			if (this.disabled) return;
			if (mediaEntry) {
				const file = createMediaClipboardFile(mediaEntry);
				if (file) {
					this.insertClipboardFile(file, mediaEntry.kind);
					return;
				}
			}
			if (txt && typeof txt == 'string') {
				let textNode = document.createTextNode(txt);
				range.insertNode(textNode)
				range.collapse();
			}
			this.syncEditorHeight();
		},
		pasteScreenShot(buffer) {
			let id = this.generateId();
			let file = new File([buffer], 'screenShot-' + id + '.png', { type: "image/png" }); //type为图片的格式
			this.insertImageFile(file, id);
		},
		selectElement(element, endOffset) {
			let selection = window.getSelection();
			// 插入元素可能不是立即执行的，vue可能会在插入元素后再更新dom
			this.$nextTick(() => {
				if (this.disabled) return;
				let t1 = document.createRange();
				const isTextNode = element && element.nodeType === 3;
				const resolvedOffset = typeof endOffset === 'number'
					? endOffset
					: (isTextNode ? (element.textContent || '').length : 0);
				t1.setStart(element, resolvedOffset);
				t1.setEnd(element, resolvedOffset);
				if (!isTextNode && element.firstChild) {
					t1.selectNodeContents(element.firstChild);
				}
				t1.collapse();
				selection.removeAllRanges();
				selection.addRange(t1);
				// 需要时自动聚焦
				if (element.focus) {
					element.focus();
				} else if (this.$refs.content) {
					this.$refs.content.focus();
				}
				this.updateRange();
			})
		},
		createEmptyLine() {
			let line = document.createElement('div');
			line.appendChild(document.createElement('br'));
			return line;
		},
		moveCaretToEmptyLine() {
			const content = this.$refs.content;
			if (this.disabled || !content) {
				return;
			}
			let line = content.firstChild;
			if (!line || line.nodeName !== 'DIV') {
				line = this.createEmptyLine();
				content.innerHTML = '';
				content.appendChild(line);
			}
			const selection = window.getSelection();
			const range = document.createRange();
			range.setStart(line, 0);
			range.setEnd(line, 0);
			selection.removeAllRanges();
			selection.addRange(range);
			content.focus();
			this.updateRange();
		},
		isEditorEffectivelyEmpty() {
			const content = this.$refs.content;
			if (!content) {
				return true;
			}
			const meaningfulElement = Array.from(content.querySelectorAll('*')).some((node) => {
				const nodeName = node.nodeName.toLowerCase();
				if (nodeName === 'br') {
					return false;
				}
				if (nodeName === 'div' && !node.dataset.fileId) {
					const normalizedText = (node.textContent || '').replace(/[\u00A0\u200B]/g, '').trim();
					const onlyBreakChild = node.childNodes.length === 1 && node.firstChild && node.firstChild.nodeName === 'BR';
					return normalizedText !== '' || !onlyBreakChild;
				}
				return true;
			});
			if (meaningfulElement) {
				return false;
			}
			const normalizedText = (content.textContent || '').replace(/[\u00A0\u200B]/g, '').trim();
			return normalizedText === '';
		},
		onCompositionEnd(e) {
			this.compositionFlag = false;
			this.onEditorInput(e);
			this.syncEditorHeight();
		},
		onKeydown(e) {
			if (this.disabled) {
				e.preventDefault();
				return;
			}
			if (e.keyCode === 13) {
				e.preventDefault();
				e.stopPropagation();
				if (this.atIng) {
					this.$refs.atBox.select();
					return;
				}
				if (e.ctrlKey) {
					let selection = window.getSelection();
					let currentRange = selection.getRangeAt(0);
					let currentContainer = currentRange.endContainer;
					// 如果光标在零宽度空格节点中，先移动到父节点，避免影响 newLine() 的识别
					if (currentContainer.nodeType === 3 && currentContainer.textContent === '\u200B') {
						let parent = currentContainer.parentNode;
						let offset = Array.from(parent.childNodes).indexOf(currentContainer);
						currentRange.setStart(parent, offset);
						currentRange.setEnd(parent, offset);
						currentRange.collapse(true);
						selection.removeAllRanges();
						selection.addRange(currentRange);
					}
					let line = this.newLine();
					// 使用零宽度空格节点（\u200B）用于光标定位，不可见，不影响对齐
					let zeroWidthSpace = document.createTextNode('\u200B');
					line.appendChild(zeroWidthSpace);
					// 同步设置光标位置，确保连续换行时能正确定位
					let range = document.createRange();
					range.setStart(zeroWidthSpace, 0);
					range.setEnd(zeroWidthSpace, 0);
					range.collapse(true);
					selection.removeAllRanges();
					selection.addRange(range);
					// 同步更新 blurRange，确保下次操作使用正确位置
					this.blurRange = range.cloneRange();
					this.isEmpty = false;
				} else {
					// 中文输入标记
					if (this.compositionFlag) {
						return;
					}
					this.submit();
				}
				return;
			}
			// 删除键
			if (e.keyCode === 8) {
				// 等待dom更新
				setTimeout(() => {
					// 空dom时，需要刷新dom
					if (this.isEditorEffectivelyEmpty()) {
						this.empty();
						this.isEmpty = true;
					} else {
						this.isEmpty = false;
					}
					this.syncEditorHeight();
				})
			}
			// at框打开时，上下键移动特殊处理
			if (this.atIng) {
				if (e.keyCode === 38) {
					e.preventDefault();
					e.stopPropagation();
					this.$refs.atBox.moveUp();
				}
				if (e.keyCode === 40) {
					e.preventDefault();
					e.stopPropagation();
					this.$refs.atBox.moveDown();
				}
			}

		},
		onAtSelect(member) {
			if (this.disabled) return;
			this.atIng = false;
			// 选中输入的 @xx 符
			let blurRange = this.blurRange;
			let endContainer = blurRange.endContainer
			let startOffset = endContainer.data.indexOf("@" + this.atSearchText);
			let endOffset = startOffset + this.atSearchText.length + 1;
			blurRange.setStart(blurRange.endContainer, startOffset);
			blurRange.setEnd(blurRange.endContainer, endOffset);
			blurRange.deleteContents()
			blurRange.collapse();
			this.focus();
			// 创建元素节点
			let element = document.createElement('SPAN')
			element.className = "chat-at-user";
			element.dataset.id = member.userId;
			element.contentEditable = 'false'
			element.innerText = `@${member.showNickName}`
			blurRange.insertNode(element)
			// 光标移动到末尾
			blurRange.collapse()

			// 插入空格
			let textNode = document.createTextNode('\u00A0');
			blurRange.insertNode(textNode);

			blurRange.collapse()
			this.atSearchText = "";
			this.selectElement(textNode, 1);
		},
		onEditorInput(e) {
			if (this.disabled) return;
			this.isEmpty = false;
			this.syncEditorHeight();
			if (this.$props.groupMembers && !this.compositionFlag) {
				let selection = window.getSelection()
				if (!selection || selection.rangeCount === 0) {
					return;
				}
				let range = selection.getRangeAt(0);
				// 截取@后面的名称作为过滤条件，并以空格结束
				let endContainer = range.endContainer;
				let endOffset = range.endOffset;
				let textContent = endContainer.textContent;
				let startIndex = -1;
				for (let i = endOffset; i >= 0; i--) {
					if (textContent[i] === '@') {
						startIndex = i;
						break;
					}
				}
				// 没有at符号，则关闭弹窗
				if (startIndex === -1) {
					this.$refs.atBox.close();
					return;
				}

				let endIndex = endOffset;
				for (let i = endOffset; i < textContent.length; i++) {
					if (textContent[i] === ' ') {
						endIndex = i;
						break;
					}
				}
				this.atSearchText = textContent.substring(startIndex + 1, endIndex).trim();
				// 打开选择弹窗
				if (this.atSearchText == '') {
					this.showAtBox(e)
				}
			}
		},
		onClickInput() {
			this.updateRange();
		},
		onKeyup() {
			this.updateRange();
		},
		onMousedown() {
			if (this.atIng) {
				this.$refs.atBox.close();
				this.atIng = false;
			}
		},
		insertEmoji(emojiText) {
			if (this.disabled) return;
			let emojiElement = document.createElement('img');
			emojiElement.className = 'emoji-normal no-text';
			emojiElement.dataset.emojiCode = emojiText;
			emojiElement.src = this.$emo.textToUrl(emojiText);

			let blurRange = this.blurRange;
			if (!blurRange) {
				this.focus();
				this.updateRange();
				blurRange = this.blurRange;
			}
			if (blurRange.startContainer !== blurRange.endContainer || blurRange.startOffset !== blurRange.endOffset) {
				blurRange.deleteContents();
			}
			blurRange.insertNode(emojiElement);
			blurRange.collapse()

			let textNode = document.createTextNode('\u00A0');
			blurRange.insertNode(textNode)
			blurRange.collapse()

			this.selectElement(textNode);
			this.isEmpty = false;
			this.syncEditorHeight();
		},
		generateId() {
			return this.currentId++;
		},
		createFile(filePush) {
			let file = filePush.file;
			let fileId = filePush.fileId;
			const fileTypeMeta = resolveFileTypeMeta(getFileExtension(file.name));
			let container = document.createElement('div');
			container.className = 'chat-file-container no-text';
			container.contentEditable = 'false';
			container.dataset.fileId = fileId;
			container.title = file.name;

			let left = document.createElement('div');
			left.className = `file-position-left file-position-left--${fileTypeMeta.tone}`;
			container.appendChild(left);

			let iconLabel = document.createElement('span');
			iconLabel.className = 'file-position-left__label';
			iconLabel.innerText = fileTypeMeta.shortLabel;
			left.appendChild(iconLabel);

			let right = document.createElement('div');
			right.className = 'file-position-right';
			container.appendChild(right);

			let fileName = document.createElement('div');
			fileName.className = 'file-name';
			fileName.innerText = file.name;

			let fileMeta = document.createElement('div');
			fileMeta.className = 'file-meta';

			let fileType = document.createElement('span');
			fileType.className = `file-type-tag file-type-tag--${fileTypeMeta.tone}`;
			fileType.innerText = fileTypeMeta.fullLabel;

			let fileSize = document.createElement('div');
			fileSize.className = 'file-size';
			fileSize.innerText = this.sizeConvert(file.size);

			fileMeta.appendChild(fileType);
			fileMeta.appendChild(fileSize);

			right.appendChild(fileName);
			right.appendChild(fileMeta);

			return container;
		},
		ensureSelectionRange() {
			if (this.disabled) return null;
			let selection = window.getSelection();
			if (!selection || selection.rangeCount === 0) {
				this.moveCursorToEnd();
				selection = window.getSelection();
			}
			if (!selection || selection.rangeCount === 0) {
				return null;
			}
			return selection.getRangeAt(0);
		},
		extractClipboardFiles(clipboardData) {
			if (!clipboardData) {
				return [];
			}
			const files = [];
			const items = clipboardData.items || [];
			if (items.length) {
				for (let i = 0; i < items.length; i++) {
					const item = items[i];
					if (!item || item.kind !== 'file') {
						continue;
					}
					const file = item.getAsFile();
					if (file) {
						files.push(file);
					}
				}
			}
			if (files.length === 0 && clipboardData.files && clipboardData.files.length) {
				for (let i = 0; i < clipboardData.files.length; i++) {
					const file = clipboardData.files[i];
					if (file) {
						files.push(file);
					}
				}
			}
			return files;
		},
		insertClipboardFile(file, kind) {
			if (!file) {
				return;
			}
			if ((kind || '').indexOf('image') === 0 || file.type.indexOf('image/') === 0) {
				this.insertImageFile(file);
				return;
			}
			this.insertAttachmentFile(file);
		},
		insertImageFile(file, nextId) {
			if (this.disabled) return;
			const fileId = nextId !== undefined ? nextId : this.generateId();
			let imagePush = {
				fileId,
				file,
				url: URL.createObjectURL(file)
			};
			this.imageList[imagePush.fileId] = imagePush;
			let line = this.newLine();
			let imageElement = document.createElement('img');
			imageElement.className = 'chat-image no-text';
			imageElement.src = imagePush.url;
			imageElement.dataset.imgId = imagePush.fileId;
			line.appendChild(imageElement);
			let after = document.createTextNode('\u00A0');
			line.appendChild(after);
			this.selectElement(after, 1);
			this.syncEditorHeight();
		},
		insertAttachmentFile(file) {
			if (this.disabled) return;
			let filePush = {
				fileId: this.generateId(),
				file
			};
			this.fileList[filePush.fileId] = filePush;
			let line = this.newLine();
			let fileElement = this.createFile(filePush);
			line.appendChild(fileElement);
			let after = document.createTextNode('\u00A0');
			line.appendChild(after);
			this.selectElement(after, 1);
			this.syncEditorHeight();
		},
		sizeConvert(len) {
			if (len < 1024) {
				return len + 'B';
			} else if (len < 1024 * 1024) {
				return (len / 1024).toFixed(2) + 'KB';
			} else if (len < 1024 * 1024 * 1024) {
				return (len / 1024 / 1024).toFixed(2) + 'MB';
			} else {
				return (len / 1024 / 1024 / 1024).toFixed(2) + 'GB';
			}
		},
		updateRange() {
			if (this.disabled) return;
			let selection = window.getSelection();
			if (!selection || selection.rangeCount === 0) {
				return;
			}
			this.blurRange = selection.getRangeAt(0);
		},
		resolveEditorHeightBound(variableName, fallbackValue) {
			const element = this.$refs.content;
			if (!element || typeof window === 'undefined') {
				return fallbackValue;
			}
			const parsed = Number.parseFloat(window.getComputedStyle(element).getPropertyValue(variableName));
			return Number.isFinite(parsed) ? parsed : fallbackValue;
		},
		syncEditorHeight() {
			this.$nextTick(() => {
				const element = this.$refs.content;
				if (!element) {
					return;
				}
				const minHeight = this.resolveEditorHeightBound('--chat-editor-min-height', 56);
				const maxHeight = this.resolveEditorHeightBound('--chat-editor-max-height', 176);
				element.style.height = 'auto';
				const nextHeight = Math.max(minHeight, Math.min(element.scrollHeight, maxHeight));
				this.editorHeight = nextHeight;
				element.style.height = `${nextHeight}px`;
			});
		},
		newLine() {
			let selection = window.getSelection();
			let divElement = document.createElement('div');
			if (!selection || selection.rangeCount === 0) {
				this.$refs.content.append(divElement);
				return divElement;
			}
			let range = selection.getRangeAt(0);
			let endContainer = range.endContainer;
			let parentElement = endContainer.parentElement;
			if (endContainer.nodeType === 1 && endContainer.parentElement === this.$refs.content) {
				const currentLine = endContainer;
				const onlyBreakChild = currentLine.childNodes.length === 1 && currentLine.firstChild && currentLine.firstChild.nodeName === 'BR';
				if (onlyBreakChild) {
					currentLine.innerHTML = '';
					return currentLine;
				}
				if (range.endOffset === currentLine.childNodes.length) {
					currentLine.insertAdjacentElement('afterend', divElement);
					return divElement;
				}
			}
			if (parentElement.parentElement === this.$refs.content) {
				divElement.innerHTML = endContainer.textContent.substring(range.endOffset).trim();
				endContainer.textContent = endContainer.textContent.substring(0, range.endOffset);
				// 插入到当前div（当前行）后面
				parentElement.insertAdjacentElement('afterend', divElement);
			} else {
				divElement.innerHTML = '';
				this.$refs.content.append(divElement);
			}
			return divElement;
		},
		clear() {
			this.empty();
			this.imageList = [];
			this.fileList = [];
			this.$refs.atBox.close();
			this.syncEditorHeight();
		},
		empty(shouldFocus = true) {
			this.$refs.content.innerHTML = "";
			this.$refs.content.appendChild(this.createEmptyLine());
			this.isEmpty = true;
			this.blurRange = null;
			if (shouldFocus) {
				this.$nextTick(() => this.moveCaretToEmptyLine());
			}
			this.syncEditorHeight();
		},
		showAtBox() {
			if (this.disabled) return;
			this.atIng = true;
			// show之后会自动更新当前搜索的text
			// this.atSearchText = "";
			let selection = window.getSelection()
			let range = selection.getRangeAt(0)
			// 光标所在坐标
			let pos = range.getBoundingClientRect();
			this.$refs.atBox.open({
				x: pos.x,
				y: pos.y
			})
			// 记录光标所在位置
			this.updateRange();
		},
		submit() {
			if (this.disabled) return;
			let nodes = this.$refs.content.childNodes;
			let fullList = [];
			let tempText = '';
			let atUserIds = [];
			let each = (nodes) => {
				for (let i = 0; i < nodes.length; i++) {
					let node = nodes[i];
					if (!node) {
						continue;
					}
					if (node.nodeType === 3) {
						tempText += node.textContent;
						continue;
					}
					let nodeName = node.nodeName.toLowerCase();
					if (nodeName === 'script') {
						continue;
					}
					let text = tempText.trim();
					if (nodeName === 'img') {
						let imgId = node.dataset.imgId;
						if (imgId) {
							if (text) {
								fullList.push({
									type: 'text',
									content: text,
									atUserIds: atUserIds
								})
								tempText = '';
								atUserIds = []
							}
							fullList.push({
								type: 'image',
								content: this.imageList[imgId]
							})
						} else {
							let emojiCode = node.dataset.emojiCode;
							tempText += emojiCode;
						}
					} else if (nodeName === 'div') {
						let fileId = node.dataset.fileId
						// 文件
						if (fileId) {
							if (text) {
								fullList.push({
									type: 'text',
									content: text,
									atUserIds: atUserIds
								})
								tempText = '';
								atUserIds = []
							}
							fullList.push({
								type: 'file',
								content: this.fileList[fileId]
							})
						} else {
							tempText += '\n';
							each(node.childNodes);
						}
					} else if (nodeName === 'span') {
						if (node.dataset.id) {
							tempText += node.innerHTML;
							atUserIds.push(node.dataset.id)
						} else if (node.outerHtml) {
							tempText += node.outerHtml;
						}
					}
				}
			}
			each(nodes)
			let text = tempText.trim();
			if (text !== '') {
				fullList.push({
					type: 'text',
					content: text,
					atUserIds: atUserIds
				})
			}
			this.$emit('submit', fullList);
		},
		focus() {
			const content = this.$refs.content;
			if (this.disabled || !content) {
				return;
			}
			if (this.isEmpty) {
				this.moveCaretToEmptyLine();
				return;
			}
			content.focus();
			this.updateRange();
		},
		saveDraft(chat) {
			if (!chat) return;
			const draft = {
				innerHTML: this.$refs.content.innerHTML,
				imageList: this.imageList,
				fileList: this.fileList,
				isEmpty: this.isEmpty
			}
			const key = this.buildDraftKey(chat);
			this.draftMap.set(key, draft);
			this.chatStore.setDraftText(chat, this.draftText())

		},
		restoreDraft(chat) {
			if (!chat) return;
			const key = this.buildDraftKey(chat);
			const draft = this.draftMap.get(key);
			if (draft && !draft.isEmpty) {
				this.$refs.content.innerHTML = draft.innerHTML;
				this.imageList = draft.imageList;
				this.fileList = draft.fileList;
				this.isEmpty = draft.isEmpty;
				this.chatStore.setDraftText(chat, "");
				// 将光标移动到末尾
				this.moveCursorToEnd();
			} else {
				this.clear();
			}
			this.syncEditorHeight();
		},
		moveCursorToEnd() {
			if (this.disabled) return;
			if (this.isEmpty) {
				this.moveCaretToEmptyLine();
				return;
			}
			const content = this.$refs.content;
			const range = document.createRange();
			const selection = window.getSelection();
			// 将光标设置到 content 元素的末尾
			range.selectNodeContents(content);
			// false表示折叠到范围的末尾
			range.collapse(false); 
			selection.removeAllRanges();
			selection.addRange(range);
			content.focus();
			this.updateRange();
		},
		buildDraftKey(chat) {
			return chat.type + '-' + chat.targetId;
		},
		draftText() {
			let nodes = this.$refs.content.childNodes;
			let tempText = '';
			let each = (nodes) => {
				for (let i = 0; i < nodes.length; i++) {
					let node = nodes[i];
					if (!node) {
						continue;
					}
					if (node.nodeType === 3) {
						tempText += node.textContent;
						continue;
					}
					let nodeName = node.nodeName.toLowerCase();
					if (nodeName === 'script') {
						continue;
					}
					if (nodeName === 'img') {
						let imgId = node.dataset.imgId;
						if (imgId) {
							tempText += '[图片]';
						} else {
							let emojiCode = node.dataset.emojiCode;
							tempText += emojiCode;
						}
					} else if (nodeName === 'div') {
						let fileId = node.dataset.fileId
						// 文件
						if (fileId) {
							tempText += '[文件]';
						} else {
							tempText += '\n';
							each(node.childNodes);
						}
					} else if (nodeName === 'span') {
						if (node.dataset.id) {
							tempText += node.innerHTML;
						} else if (node.outerHtml) {
							tempText += node.outerHtml;
						}
					}
				}
			}
			each(nodes)
			return tempText.trim();
		}
	}
}
</script>

<style lang="scss">
.chat-input-area {
	--chat-editor-min-height: 64px;
	--chat-editor-max-height: 208px;
	--chat-editor-line-height: 22px;
	width: 100%;
	min-height: calc(var(--chat-editor-min-height) + 18px);
	max-height: calc(var(--chat-editor-max-height) + 18px);
	position: relative;
	display: flex;
	align-items: stretch;
	box-sizing: border-box;
	padding: 9px 12px;
	border-radius: 10px;
	border: 1px solid var(--im-surface-panel-border);
	background: var(--im-chat-bg-alt);
	box-shadow: none;
	transition: border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease;

	&:focus-within {
		border-color: rgba(var(--im-primary-rgb), 0.18);
		background: var(--im-chat-bg);
		box-shadow: 0 0 0 2px rgba(var(--im-primary-rgb), 0.05);
	}

	.edit-container {
		position: relative;
		width: 100%;
		min-height: var(--chat-editor-min-height);
		max-height: var(--chat-editor-max-height);
		height: var(--chat-editor-min-height);
		outline: none;
		padding: 0 2px;
		box-sizing: border-box;
		line-height: var(--chat-editor-line-height);
		font-size: 14px;
		color: var(--im-text-color-primary);
		text-align: left;
		overflow-y: auto;
		white-space: pre-wrap;
		caret-color: var(--im-color-primary);

		&.is-empty::before {
			content: attr(data-placeholder);
			position: absolute;
			top: 0;
			left: 2px;
			right: 10px;
			line-height: var(--chat-editor-line-height);
			font-size: 13px;
			color: var(--im-text-color-lighter);
			pointer-events: none;
			white-space: pre-wrap;
			overflow: hidden;
			text-overflow: ellipsis;
		}

		// 单独一行时，无法在前面输入的bug
		>div:before {
			content: "\00a0";
			font-size: 14px;
			position: absolute;
			top: 0;
			left: 0;
		}

		.chat-image {
			display: block;
			max-width: 200px;
			max-height: 100px;
			border: 1px solid var(--im-border-light);
			cursor: pointer;
		}

		.chat-file-container {
			display: inline-flex;
			align-items: center;
			gap: 12px;
			max-width: 72%;
			min-width: 220px;
			padding: 10px 12px;
			border: 1px solid rgba(var(--im-primary-rgb), 0.16);
			background: var(--im-chat-bg);
			border-radius: 12px;
			box-shadow: none;

			.file-position-left {
				display: flex;
				width: 42px;
				height: 42px;
				align-items: center;
				justify-content: center;
				flex-shrink: 0;
				border-radius: 12px;
				background: rgba(var(--im-black-rgb), 0.05);
				border: 1px solid rgba(var(--im-black-rgb), 0.06);

				.file-position-left__label {
					font-size: 12px;
					line-height: 1;
					font-weight: 700;
					letter-spacing: 0.6px;
					color: var(--im-text-color-secondary);
				}
			}

			.file-position-left--primary {
				background: rgba(var(--im-primary-rgb), 0.08);
				border-color: rgba(var(--im-primary-rgb), 0.12);
			}

			.file-position-left--primary .file-position-left__label {
				color: var(--im-color-primary);
			}

			.file-position-left--success {
				background: rgba(var(--im-success-rgb, 103, 194, 58), 0.1);
				border-color: rgba(var(--im-success-rgb, 103, 194, 58), 0.14);
			}

			.file-position-left--success .file-position-left__label {
				color: var(--im-color-success, #67c23a);
			}

			.file-position-left--warning {
				background: rgba(var(--im-warning-rgb, 230, 162, 60), 0.1);
				border-color: rgba(var(--im-warning-rgb, 230, 162, 60), 0.14);
			}

			.file-position-left--warning .file-position-left__label {
				color: var(--im-color-warning, #e6a23c);
			}

			.file-position-left--danger {
				background: rgba(var(--im-danger-rgb), 0.08);
				border-color: rgba(var(--im-danger-rgb), 0.12);
			}

			.file-position-left--danger .file-position-left__label {
				color: var(--im-color-danger);
			}

			.file-position-left--teal {
				background: rgba(20, 184, 166, 0.1);
				border-color: rgba(20, 184, 166, 0.14);
			}

			.file-position-left--teal .file-position-left__label {
				color: #0f766e;
			}

			.file-position-right {
				flex: 1;
				min-width: 0;

				.file-name {
					display: -webkit-box;
					margin-bottom: 6px;
					overflow: hidden;
					-webkit-box-orient: vertical;
					-webkit-line-clamp: 2;
					word-break: break-all;
					line-height: 20px;
					font-size: 14px;
					font-weight: 600;
					color: var(--im-text-color-primary);
				}

				.file-meta {
					display: flex;
					align-items: center;
					gap: 6px;
					flex-wrap: wrap;
				}

				.file-type-tag,
				.file-size {
					display: inline-flex;
					align-items: center;
					height: 20px;
					padding: 0 6px;
					border-radius: 999px;
					font-size: 12px;
					font-weight: 500;
				}

				.file-size {
					background: rgba(var(--im-black-rgb), 0.04);
					color: var(--im-text-color-light);
				}

				.file-type-tag {
					background: rgba(var(--im-black-rgb), 0.06);
					color: var(--im-text-color-secondary);
				}

				.file-type-tag--primary {
					background: rgba(var(--im-primary-rgb), 0.1);
					color: var(--im-color-primary);
				}

				.file-type-tag--success {
					background: rgba(var(--im-success-rgb, 103, 194, 58), 0.12);
					color: var(--im-color-success, #67c23a);
				}

				.file-type-tag--warning {
					background: rgba(var(--im-warning-rgb, 230, 162, 60), 0.12);
					color: var(--im-color-warning, #e6a23c);
				}

				.file-type-tag--danger {
					background: rgba(var(--im-danger-rgb), 0.1);
					color: var(--im-color-danger);
				}

				.file-type-tag--teal {
					background: rgba(20, 184, 166, 0.12);
					color: #0f766e;
				}
			}
		}

		.chat-at-user {
			color: var(--im-color-primary);
			display: inline-block;
			border-radius: 5px;
			background: rgba(var(--im-primary-rgb), 0.10);
			padding: 0 3px;
		}
	}
}

@media (max-width: 640px) {
	.chat-input-area {
		--chat-editor-min-height: 56px;
		--chat-editor-max-height: 160px;
		min-height: calc(var(--chat-editor-min-height) + 16px);
		padding-top: 7px;
		padding-bottom: 7px;
	}
}
</style>
